import React, { forwardRef } from "react";
import styled from "styled-components";
import IEditableProps from "../../utilities/IEditableProps";
import IStyledProps from "../../utilities/IStyledProps";
import { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import { Button } from "react-bootstrap";
import ImgHollowPlus from "../../images/expander-button-hollow-plus.svg";
import ImgHollowMinus from "../../images/expander-button-hollow-minus.svg";
import ImgSolidPlus from "../../images/expander-button-solid-plus.svg";
import ImgSolidMinus from "../../images/expander-button-solid-minus.svg";

export interface IFullWidthExpanderProps extends IEditableProps, IStyledProps {
    title?: string;
    subtitle?: string;
    open?: boolean;
    onOpen?: (id: string) => void;
    onClose?: (id: string) => void;
    id?: string;
    navId?: string;
    innerClassname: string;
    children;
}

const ExpanderTab = styled.div`
    .expand-button {
        width: 40px;
        height: 40px;
        padding: 0;
        border: none;
        border-radius: 50%;
    }
    .expand-button--closed {
        background: transparent url(${ImgHollowPlus}) center center no-repeat;
    }
    .expand-button--expanded {
        background: transparent url(${ImgHollowMinus}) center center no-repeat;
    }
    &:hover {
        .expand-button--closed {
            background-image: url(${ImgSolidPlus});
        }
        .expand-button--expanded {
            background-image: url(${ImgSolidMinus});
        }
    }
`;

const FullWidthExpander = forwardRef((props: IFullWidthExpanderProps, ref: any) => {
    const [open, setOpen] = useState(props.open);
    const [height, setHeight] = useState(0);
    const windowObj = typeof window !== 'undefined' ? window : {} as any;
    const currentHash = windowObj.location?.hash;
    const hash = `#${props.navId}`;
    const content = useRef(null);

    useEffect(() => {
        if (open) {
            setHeight(content.current.scrollHeight);
        } else {
            setHeight(0);
        }
    }, [open]);

    useEffect(() => {
        if (props.open !== open) {
            setOpen(props.open);
        }
    }, [props.open]);

    useEffect(() => {
        if (props.navId && currentHash === hash && !open) {
            openExpander();
        } else if (props.navId && currentHash !== hash && open) {
            closeExpander();
        }
    }, [windowObj.location?.hash]);

    function openExpander() {
        if (typeof props.onOpen === "function") {
            props.onOpen(props.id);
        }

        setOpen(true);
    }

    function closeExpander() {
        if (typeof props.onClose === "function") {
            props.onClose(props.id);
        }

        setOpen(false);
    }

    function handleHeaderClick() {
        if (open) {
            closeExpander();
        } else {
            if (props.navId) {
                window.location.hash = hash;
            }

            openExpander();
        }
    }

    return (
        <div id={props.navId} data-id={props.cmsId} data-nav-id={props.navId} ref={ref} className={classNames(props.className, props.editorClassObj)}>
            <ExpanderTab className={classNames("expander-tab bg-gray-300 text-center row align-items-center justify-content-center p-6", { 'is-open': open })} onClick={handleHeaderClick}>
                <div className="mb-2 col-12">
                    <h2 className="text-primary">{props.title}</h2>
                    {props.subtitle && <p className="text-muted">{props.subtitle}</p>}
                </div>
                <div className="col-12">
                    <Button aria-label="Toggle content visibility" className={classNames("expand-button", {"expand-button--expanded": open, "expand-button--closed": !open})} />
                </div>
            </ExpanderTab>
            <div
                className={
                    classNames(
                        "expander-content",
                        "bg-white",
                        "overflow-hidden",
                        { "content-visible": open }
                    )
                }
                aria-hidden={!open}
                style={{ height }} ref={content}>
                <div className={props.innerClassname}>
                    {props.children}
                </div>
            </div>
        </div >
    );
});

export default styled(FullWidthExpander)`
    .expander-content {
        transition: height .5s ease;
        border-bottom: 3px solid white;
    }

   .expander-tab {
        min-height: 250px;
        cursor: pointer;

        .btn-circle-sm {
            width: 40px;
            height: 40px;
            padding: 1px;
            border-radius: 20px;
        }
   }
`;

(global as any).FullWidthExpander = FullWidthExpander;